// src/components/FormHeader.tsx
import {
  Button,
  tokens,
  RatingDisplay,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  PopoverProps,
  OnOpenChangeData,
  Rating,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
} from "@fluentui/react-components";
import {
  Save20Regular,
  Delete20Regular,
  Share20Regular,
  Add20Regular,
  Building20Regular,
  Person20Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  Mail20Regular,
  Edit20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Document20Regular,
  ArrowUpload20Regular,
  ArrowDownload20Regular,
} from "@fluentui/react-icons";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useEffect, useRef, useState } from "react";
import { apiUrl } from "../utils/api";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import { alleFenster } from "./windowManager/browserFenster/fensterRegister";

interface Authority {
  id?: number;
  erstinformation: boolean;
  werbewiederspruch: boolean;
  signed?: boolean;
  // `| null`, weil die Spalten nullable sind und die API bei fehlendem Scan
  // wirklich `null` liefert (siehe ContactAuthority in types/Contact.ts). Reine
  // Typ-Angleichung an den Bestand — an dieser Legacy-Maske ändert sich nichts.
  documentUrl?: string | null;
  documentName?: string | null;
}

export interface FormHeaderProps {
  title: string;
  typ?: string | number; // "Firma", "Privat", "Interessent" or number
  relevanz?: number;
  phase?: string;
  value?: string | number;
  authority?: Authority;
  jahresgespraech?: string; // Date string
  empfehlungsgeber?: string;
  status?: { id?: number; status?: string }; // Add status prop
  kontaktStatus?: string;
  onSave?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  onAdd?: () => void;
  onOpenNotes?: () => void;
  notesCount?: number;
  onPhaseChange?: (phase: string) => void;
  onJahresgespraechChange?: (date: string) => void;
  onEmpfehlungsgeberClick?: () => void;
  onRelevanzChange?: (value: number) => void;
  icon?: React.ReactNode;
  contactId?: string | number;
}

export const FormHeader: React.FC<FormHeaderProps> = ({
  title,
  typ,
  relevanz,
  phase,
  value,
  authority,
  jahresgespraech,
  empfehlungsgeber,
  status,
  kontaktStatus,
  onSave,
  onDelete,
  onShare,
  onAdd,
  onOpenNotes,
  notesCount,
  onPhaseChange,
  onJahresgespraechChange,
  onEmpfehlungsgeberClick,
  onRelevanzChange,
  icon,
  contactId,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [localJahresgespraech, setLocalJahresgespraech] = useState<Date | null>(
    jahresgespraech ? new Date(jahresgespraech) : null
  );
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1200 : window.innerWidth,
  );
  // Determine if Interesent based on logic matching ContactTable
  const isInteressent = status?.status === "Interessent" || kontaktStatus === "Interessent";
  const isCompany = typ === "Firma" || typ === 1 || (typeof typ === 'object' && (typ as any).type === 'Firma');
  const isMobile = viewportWidth < 768;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setViewportWidth(window.innerWidth);
    alleFenster.addEventListener("resize", handleResize);
    return () => alleFenster.removeEventListener("resize", handleResize);
  }, []);

  // Format value as currency if it's a number
  const formattedValue = typeof value === 'number'
    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)
    : value;

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* Icon bar centered */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          padding: `${isMobile ? tokens.spacingVerticalS : tokens.spacingVerticalM} 0`,
          gap: isMobile ? tokens.spacingHorizontalS : tokens.spacingHorizontalL,
        }}
      >
        <Button
          appearance="subtle"
          icon={<Save20Regular />}
          aria-label="Save"
          color="red"
          onClick={onSave}
        />
        <Button
          appearance="subtle"
          icon={<Delete20Regular color="red" />}
          aria-label="Delete"
          onClick={onDelete}
        />
        <Button
          appearance="subtle"
          icon={<Add20Regular />}
          aria-label="Add"
          onClick={onAdd}
        />
        <div style={{ position: 'relative', display: 'flex' }}>
          <Button
            appearance="subtle"
            icon={<Edit20Regular />}
            aria-label="Notes"
            onClick={onOpenNotes}
          />
          {notesCount !== undefined && (
            <span style={{
              position: 'absolute',
              top: -5,
              right: -5,
              backgroundColor: 'red',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold'
            }}>
              {notesCount}
            </span>
          )}
        </div>
        <Share20Regular aria-label="Share" onClick={onShare} />
      </div>

      {/* Title + Icon below, left aligned */}
      <div
        style={{
          paddingBottom: isMobile ? 12 : 20,
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? tokens.spacingVerticalS : tokens.spacingHorizontalM,
          minWidth: 0,
        }}
      >
        {icon && <div style={{ flexShrink: 0 }}>{icon}</div>}
        <div style={{ minWidth: 0, width: "100%" }}>
          <h2
            style={{
              margin: 0,
              color: tokens.colorNeutralForeground1,
              fontSize: isMobile ? "clamp(1.6rem, 7vw, 2.1rem)" : "2rem",
              lineHeight: 1.05,
              wordBreak: "break-word",
            }}
          >
            {title} {"-"} {status?.status}
          </h2>

          <div
            style={{
              display: "flex",
              gap: isMobile ? tokens.spacingHorizontalS : tokens.spacingHorizontalM,
              flexWrap: "wrap",
              marginTop: tokens.spacingVerticalS,
              minWidth: 0,
            }}
          >
            {/* Firma / Privat Icon */}
            <span style={tagStyle}>
              {isCompany ? <Building20Regular /> : <Person20Regular />}
              {typeof typ === 'string' ? contactTypeLabel(typ) : (isCompany ? 'Firma' : 'Privat')}
            </span>

            {/* Relevanz or Phase */}
            {isInteressent ? (
              <div style={{ ...tagStyle, cursor: "pointer", position: "relative" }}>
                <span>{phase || "Phase wählen"}</span>
                <ChevronDown20Regular />
                <select
                  value={phase || ""}
                  onChange={(e) => onPhaseChange?.(e.target.value)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                >
                  <option value="">— wählen —</option>
                  <option value="Erstkontakt">Erstkontakt</option>
                  <option value="Angebotsphase">Angebotsphase</option>
                </select>
              </div>
            ) : onRelevanzChange ? (
              <Popover>
                <PopoverTrigger disableButtonEnhancement>
                  <div style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                    <RatingDisplay
                      value={Math.floor((relevanz || 0) * 2) / 2}
                      max={5}
                      size="small"
                      color="marigold"
                    />
                  </div>
                </PopoverTrigger>
                <PopoverSurface tabIndex={-1}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "4px" }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold, fontSize: "0.9rem" }}>Relevanz anpassen</div>
                    <Rating
                      value={relevanz || 0}
                      onChange={(_, data) => onRelevanzChange?.(data.value)}
                    />
                  </div>
                </PopoverSurface>
              </Popover>
            ) : (
              <RatingDisplay value={Math.floor((relevanz || 0) * 2) / 2} max={5} size="small" color="marigold" />
            )}

            {/* Kundenwert (only if not Interesent) */}
            {!isInteressent && formattedValue && (
              <span style={tagStyle}>{formattedValue}</span>
            )}

            {/* Vollmachten / Authorities */}
            {authority && (
              <div style={{ display: "flex", gap: isMobile ? tokens.spacingHorizontalS : tokens.spacingHorizontalM, flexWrap: "wrap" }}>
                <AuthorityMenu
                  authority={authority}
                  onSave={onSave}
                  type="erstinfo"
                  contactId={contactId}
                  tagStyle={tagStyle}
                />
                <AuthorityMenu
                  authority={authority}
                  onSave={onSave}
                  type="werbe"
                  contactId={contactId}
                  tagStyle={tagStyle}
                />
              </div>
            )}

            {/* Jahresgespräch (only if present) */}
            {/* User requirement: "letzes Jahresgespräch Empfehlungsgeber" - should be displayed? 
                User said "then letzes Jahresgespräch Empfehlungsgeber". 
                I'll add them as tags as well. 
            */}
            {/* Jahresgespräch */}
            {!isInteressent && (
              <Popover open={isPopoverOpen} onOpenChange={(_: any, data: OnOpenChangeData) => setIsPopoverOpen(data.open)}>
                <PopoverTrigger disableButtonEnhancement>
                  <div style={{ ...tagStyle, cursor: "pointer" }}>
                    <span>Jahresgespräch: {jahresgespraech ? new Date(jahresgespraech).toLocaleDateString() : "Nicht gesetzt"}</span>
                    <ChevronDown20Regular />
                  </div>
                </PopoverTrigger>

                <PopoverSurface tabIndex={-1}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "8px" }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold }}>Jahresgespräch bearbeiten</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <DatePicker
                        value={localJahresgespraech}
                        onSelectDate={(date) => setLocalJahresgespraech(date ?? null)}
                        placeholder="Datum wählen"
                        style={{ minWidth: "150px" }}
                      />
                      <Button
                        appearance="primary"
                        style={{ height: "32px" }} // Matching standard DatePicker height approximately, or use specific tokens if needed
                        onClick={() => {
                          onJahresgespraechChange?.(localJahresgespraech?.toISOString() || "");
                          setIsPopoverOpen(false);
                        }}
                      >
                        Speichern
                      </Button>
                    </div>
                  </div>
                </PopoverSurface>
              </Popover>
            )}

            {/* Empfehlungsgeber */}
            <div
              style={{ ...tagStyle, cursor: "pointer" }}
              onClick={onEmpfehlungsgeberClick}
            >
              <span>Empfehlung: {empfehlungsgeber || "Keine"}</span>
              <ChevronDown20Regular />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthorityMenu: React.FC<{
  authority: Authority,
  onSave?: () => void,
  type: 'erstinfo' | 'werbe',
  contactId?: string | number,
  tagStyle: React.CSSProperties
}> = ({ authority, onSave, type, contactId, tagStyle }) => {
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const label = type === 'erstinfo' ? 'Erstinfo' : 'Werbewiderspruch';
  const isSet = type === 'erstinfo' ? authority.erstinformation : authority.werbewiederspruch;

  /**
   * Open an authenticated document URL in a new tab.
   *
   * `window.open(url)` makes a top-level navigation, which doesn't go through
   * the global fetch wrapper that attaches our Bearer token (api.ts) — so the
   * backend would reject the request with 401. We fetch the bytes through the
   * wrapper instead, wrap them in a blob: URL, and open that. This keeps the
   * download authenticated even though the rendered URL is short-lived.
   */
  const openAuthenticated = async (path: string): Promise<void> => {
    try {
      const res = await fetch(apiUrl(path));
      if (!res.ok) {
        console.error(`Document download failed: ${res.status} ${res.statusText}`);
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const opened = window.open(objectUrl, "_blank");
      // Release the object URL once the new tab has had a chance to load it.
      // Done on a generous timer because revoking too early breaks the open
      // tab in some browsers.
      if (opened) {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      }
    } catch (err) {
      console.error("Document download failed", err);
    }
  };

  const handleDownload = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();

    // If authority is set and has a document, download that document
    if (isSet && authority.documentUrl) {
      void openAuthenticated(authority.documentUrl);
      return;
    }

    // Otherwise, download the template PDF
    let id = contactId;
    if (!id) {
      const match = window.location.pathname.match(/\/kontakte\/(\d+)/);
      id = match ? match[1] : undefined;
    }

    if (id) {
      void openAuthenticated(`contact/${id}/authority-pdf`);
    } else {
      console.error("No contact ID found for download");
    }
  };

  const handleUploadClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploading(true);
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(apiUrl("upload/contact-authority"), {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          authority.documentUrl = data.url;
          authority.documentName = data.name;
          if (type === 'erstinfo') authority.erstinformation = true;
          if (type === 'werbe') authority.werbewiederspruch = true;
          onSave?.();
        }
      } catch (error) {
        console.error("Error uploading authority document:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <>
      <Menu positioning={{ autoSize: true }}>
        <MenuTrigger disableButtonEnhancement>
          <span style={{ ...tagStyle, cursor: "pointer" }}>
            {label}
            {type === 'werbe' && <Mail20Regular />}
            {isSet ? <CheckmarkCircle20Regular color="green" /> : <DismissCircle20Regular color="red" />}
          </span>
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            {type === 'erstinfo' && (
              <MenuItem icon={<ArrowDownload20Regular />} onClick={handleDownload}>
                Herunterladen
              </MenuItem>
            )}

            <MenuItem icon={<Edit20Regular />}>
              Unterschreiben lassen
            </MenuItem>

            <MenuItem
              icon={<ArrowUpload20Regular />}
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? "Lädt..." : "Unterschriebenes Dokument hochladen"}
            </MenuItem>

            {isSet && authority.documentUrl && (
              <MenuItem
                icon={<Document20Regular />}
                onClick={() => void openAuthenticated(authority.documentUrl!)}
              >
                {authority.documentName || "Ansehen"}
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleUpload}
      />
    </>
  );
};

// Reusable style for tags
const tagStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 6px",
  borderRadius: 6,
  backgroundColor: tokens.colorNeutralBackground2,
  fontSize: "0.85rem",
  maxWidth: "100%",
  flexWrap: "wrap",
};

export default FormHeader;
